import React, { useEffect, useState } from "react";
import VendorLayout from "@/components/vendor_portal/VendorLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { KeyRound, Copy, Check, ShieldCheck, ShieldOff, Trash2 } from "lucide-react";

/**
 * iter70c · Vendor Portal Settings.
 *
 * First real card here is the Shared Team Passkey (aka vendor back-office
 * passcode) — a 5-10 character shared code any team member can type on
 * `/vendors/back-office` to sign in as the vendor company. Mirrors the
 * club-side back-office passcode UX so vendors that also work with clubs
 * feel at home.
 */
export default function VendorSettingsPage() {
  const [status, setStatus] = useState({ loading: true });
  const [newCode, setNewCode] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [copied, setCopied] = useState(false);
  const [lastSet, setLastSet] = useState(null); // holds plaintext momentarily after set

  const refreshStatus = async () => {
    try {
      const { data } = await api.get("/vendor/back-office/status");
      setStatus({ loading: false, ...data });
    } catch (e) {
      setStatus({ loading: false, configured: false });
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { refreshStatus(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setErr(""); setOk("");
    if (newCode !== confirm) { setErr("Passkeys don't match."); return; }
    if (newCode.length < 5 || newCode.length > 10) {
      setErr("Passkey must be 5 to 10 characters.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/vendor/back-office/setup", { passcode: newCode });
      setOk(status.configured ? "Passkey rotated." : "Passkey set. Share it only with your team.");
      setLastSet(newCode);
      setNewCode(""); setConfirm("");
      await refreshStatus();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const clear = async () => {
    if (!window.confirm("Remove the shared team passkey? Your team will need to sign in with email + password.")) return;
    setBusy(true); setErr(""); setOk("");
    try {
      await api.delete("/vendor/back-office/setup");
      setOk("Passkey removed.");
      setLastSet(null);
      await refreshStatus();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const copyLast = async () => {
    if (!lastSet) return;
    await navigator.clipboard.writeText(lastSet);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <VendorLayout>
      <div className="max-w-3xl" data-testid="vendor-settings-page">
        <div className="overline">Settings</div>
        <h1 className="font-serif text-3xl mt-2 text-[#1A422D]">Team Access &amp; Security</h1>
        <p className="text-sm text-[#4A4A4A] mt-2 max-w-xl">
          Manage how your team signs in to the vendor portal. Individual accounts still use email + password;
          the shared passkey below is an optional shortcut for teams that share a workstation.
        </p>

        <section className="mt-8 border border-[#E5E3D8] bg-white p-6" data-testid="vendor-back-office-passkey-card">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 text-[#B59A5A]"><KeyRound size={20} /></div>
            <div className="flex-1">
              <h2 className="font-serif text-xl text-[#1A422D]">Shared Team Passkey</h2>
              <p className="text-sm text-[#4A4A4A] mt-1">
                Anyone typing this 5–10 character passkey on{" "}
                <code className="bg-[#F3F1E9] px-1.5 py-0.5 text-[11px]">/vendors/back-office</code>{" "}
                will sign in as your company&apos;s vendor account.
              </p>

              <div className="mt-4">
                {status.loading ? (
                  <span className="text-xs text-[#7A7A7A]">Checking status…</span>
                ) : status.configured ? (
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#1A422D] bg-[#EFF3EC] text-[#1A422D] text-[11px] uppercase tracking-[0.22em]"
                       data-testid="vendor-passkey-status-configured">
                    <ShieldCheck size={13} /> Passkey configured
                    {status.set_at && (
                      <span className="text-[10px] normal-case tracking-normal text-[#4A4A4A] ml-1">
                        · set {new Date(status.set_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#B59A5A] bg-[#FBF6E8] text-[#8a6f33] text-[11px] uppercase tracking-[0.22em]"
                       data-testid="vendor-passkey-status-none">
                    <ShieldOff size={13} /> No passkey yet
                  </div>
                )}
              </div>

              {lastSet && (
                <div className="mt-4 flex items-center gap-3 p-4 bg-[#FBF6E8] border border-[#B59A5A]"
                     data-testid="vendor-passkey-just-set">
                  <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#8a6f33] font-semibold">
                      Copy &amp; share once — we won&apos;t show it again
                    </div>
                    <div className="font-mono text-lg text-[#1A1A1A] tracking-widest mt-1">{lastSet}</div>
                  </div>
                  <button
                    onClick={copyLast}
                    data-testid="vendor-passkey-copy"
                    className="inline-flex items-center gap-1.5 border border-[#1A422D] text-[#1A422D] px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#1A422D] hover:text-[#F9F8F4]"
                  >
                    {copied ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy</>}
                  </button>
                </div>
              )}

              <form onSubmit={save} className="mt-6 space-y-4">
                <div>
                  <label className="label-elegant block mb-1">
                    {status.configured ? "New passkey" : "Passkey"}
                  </label>
                  <input
                    type="text"
                    autoComplete="off"
                    value={newCode}
                    onChange={(e) => setNewCode(e.target.value.replace(/\s/g, ""))}
                    minLength={5}
                    maxLength={10}
                    data-testid="vendor-passkey-input"
                    className="input-field font-mono tracking-widest"
                    placeholder="e.g. GOLF2026"
                  />
                </div>
                <div>
                  <label className="label-elegant block mb-1">Confirm</label>
                  <input
                    type="text"
                    autoComplete="off"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value.replace(/\s/g, ""))}
                    minLength={5}
                    maxLength={10}
                    data-testid="vendor-passkey-confirm"
                    className="input-field font-mono tracking-widest"
                    placeholder="Retype"
                  />
                </div>

                {err && <div className="text-sm text-[#8a1a1a] border border-[#A33] bg-[#fbe9e9] px-3 py-2"
                             data-testid="vendor-passkey-error">{err}</div>}
                {ok && !lastSet && (
                  <div className="text-sm text-[#1A422D] border border-[#1A422D] bg-[#EFF3EC] px-3 py-2"
                       data-testid="vendor-passkey-ok">{ok}</div>
                )}

                <div className="flex items-center gap-3">
                  <button
                    type="submit"
                    disabled={busy || !newCode || !confirm}
                    data-testid="vendor-passkey-save"
                    className="bg-[#1A422D] text-[#F9F8F4] px-5 py-2.5 text-xs uppercase tracking-[0.22em] hover:bg-[#0f1a14] disabled:opacity-50"
                  >
                    {busy ? "Saving…" : status.configured ? "Rotate Passkey" : "Set Passkey"}
                  </button>
                  {status.configured && (
                    <button
                      type="button"
                      onClick={clear}
                      disabled={busy}
                      data-testid="vendor-passkey-remove"
                      className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.22em] text-[#8a1a1a] hover:text-[#5c0f0f]"
                    >
                      <Trash2 size={13} /> Remove
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </section>
      </div>
    </VendorLayout>
  );
}
